import { useState, useRef, useEffect } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../../../Context/ThemeContext';
import { useLanguage } from '../../../../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../../../../Styles/theme';
import { formatPhoneNumber } from '../../../../../Utils/phoneUtils';

import FetchModule from '../../../../Share/Network/FetchModule';

import TotalModalWrapper from '../../../../Share/Normal/TotalModalWrapper';
import UserProfile from '../../../../../Component/Normal/UserProfile';
import CalenderInput from '../../../../../Component/MemberComponent/CalenderInput';

import DefaultProfile from '../../../../../image/DefaultProfile.png';
import LoadingAnimation from '../../../../../Animation/NewLoading.json';

import ExistingTransfereeView from './ExistingTransfereeView';
import NewTransfereeView from './NewTransfereeView';

export default function MemberShipAssignModal(props) {
  const {
    memberProfile,
    memberName,
    memberPhone,
    memberCountryCode,
    membershipUsageId,
    membershipName,
    membershipStartDate,
    membershipEndDate,
    membershipPrice,
    membershipPaymentMethodText,
    membershipNotReceivedPrice,
    membershipTotalMax,
    membershipCourseTakeCount,
    rendering,
    exitClick
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t, languageMode } = useLanguage();

  const isUnlimitedSessions = membershipTotalMax === -1;

  let effectiveStartDay;

  if (membershipStartDate && moment(membershipStartDate).startOf('day').isAfter(moment().startOf('day'))) {
    effectiveStartDay = moment(membershipStartDate).startOf('day');
  }
  else {
    effectiveStartDay = moment().startOf('day');
  }

  const remainingDays = membershipEndDate ? Math.max(0, moment(membershipEndDate).startOf('day').diff(effectiveStartDay, 'days')) : 0;
  const remainingSessions = isUnlimitedSessions ? 0 : Math.max(0, (membershipTotalMax ?? 0) - (membershipCourseTakeCount ?? 0));

  let membershipPeriod = '';

  if (membershipStartDate && membershipEndDate) {
    membershipPeriod = `${moment(membershipStartDate).format(t('calendarInput.shortDateFormat'))} - ${moment(membershipEndDate).format(t('calendarInput.shortDateFormat'))}`;
  }

  const formattedPrice = (membershipPrice ?? 0).toLocaleString();
  const formattedNotReceivedPrice = (membershipNotReceivedPrice ?? 0).toLocaleString();
  const paymentMethodsText = membershipPaymentMethodText || t('memberDetail.payFree');

  const [transferDate, setTransferDate] = useState(moment().format('YYYY.MM.DD'));
  const [activeTab, setActiveTab] = useState('existing');
  const [searchValue, setSearchValue] = useState('');
  const [transferDays, setTransferDays] = useState(String(remainingDays));
  const [transferSessions, setTransferSessions] = useState(String(remainingSessions));

  const [transfereeList, setTransfereeList] = useState([]);
  const [isLoadingTransferees, setIsLoadingTransferees] = useState(true);
  const [isSavingTransfer, setIsSavingTransfer] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedTransferee, setSelectedTransferee] = useState(null);

  const [noHavePhone, setNoHavePhone] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ countryCode: '+82', flagUrl: 'https://picture.link-zone.org/flags/kr.svg', countryNameKR: '대한민국', countryNameEN: 'South Korea' });
  const [phone, setPhone] = useState('');
  const [phoneErrorMsg, setPhoneErrorMsg] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState('male');
  const [birth, setBirth] = useState('');
  const [profile, setProfile] = useState('');
  const [userId, setUserId] = useState('');
  const [isUnregisteredAfterCheck, setIsUnregisteredAfterCheck] = useState(false);
  const [memberDetail, setMemberDetail] = useState('');

  const countryCodeSelectBoxRef = useRef(null);
  const TransfereeScrollRef = useRef(null);

  // 전화번호 변경 시 미등록 상태 초기화 //
  useEffect(() => {
    if (isUnregisteredAfterCheck) setIsUnregisteredAfterCheck(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  // 양도받을 회원 목록 조회 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('member/membership-status', 'GET');

        if (responseData.status === 200) {
          setTransfereeList(responseData.data);
        }
        setIsLoadingTransferees(false);
      }
    )();
  }, []);

  const filteredTransfereeList = transfereeList.filter(m => {
    if (!searchValue) return true;
    const q = searchValue.toLowerCase();
    return (m.name || '').toLowerCase().includes(q) || (m.phone || '').includes(searchValue);
  });

  function computeTransferEndDate() {
    const days = parseInt(transferDays, 10);
    if (!transferDate || isNaN(days) || days < 1) return '-';
    return moment(transferDate, 'YYYY.MM.DD').add(days, 'days').format('YYYY.MM.DD');
  }

  function handleTransferSessionsChange(e) {
    const newValue = e.target.value.replace(/[^0-9]/g, '');
    setTransferSessions(newValue);
  }

  function onClickTransfereeItem(member) {
    setSelectedTransferee(member);
    setIsSearchOpen(false);
  }

  function onClickNoPhone() {
    setNoHavePhone(!noHavePhone);
  }

  function onClickLinkCoachIdCheck() {
    if (phone === '' || phone.length < 6) {
      setPhoneErrorMsg(t('memberRegistration.alertPhoneRequired'));
      return;
    }

    const data = { phone: phone, userId: '', profile: '' };
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataOfCoach('userInfo/detail/invalid/user', 'POST', data);

        if (responseData.status === 200) {
          setIsUnregisteredAfterCheck(false);
          setProfile(responseData.data[0].profile);
          setUserId(responseData.data[0].userId);
          setName(responseData.data[0].name);

          const genderNum = responseData.data[0].genderNum.replaceAll('*', '');

          if (genderNum === '1' || genderNum === '3') setGender('male');
          else setGender('female');

          if (genderNum === '1' || genderNum === '2' || genderNum === '5' || genderNum === '6') setBirth('19' + responseData.data[0].birth);
          else setBirth('20' + responseData.data[0].birth);

          if (countryCodeSelectBoxRef.current) {
            if (responseData.data[0].countryIso) countryCodeSelectBoxRef.current.setByIso(responseData.data[0].countryIso);
            else if (responseData.data[0].countryCode) countryCodeSelectBoxRef.current.setByCode(responseData.data[0].countryCode);
          }
        }
        else if (responseData.status === 201) alert(t('memberRegistration.alertMemberMismatch'));
        else if (responseData.status === 204) {
          setIsUnregisteredAfterCheck(true);
          alert(t('memberRegistration.alertMemberNotFound'));
        }
        else if (responseData.status === 401) alert(t('memberRegistration.alertLoginRequired'));
      }
    )();
  }

  function handleTransferDaysChange(e) {
    const newValue = e.target.value.replace(/[^0-9]/g, '');
    setTransferDays(newValue);
  }

  function onClickSave() {
    const validityDays = parseInt(transferDays, 10);

    if (!validityDays || validityDays < 1) {
      alert(t('memberDetail.assignModalAlertInvalidDays'));
      return;
    }

    const totalMaxValue = isUnlimitedSessions ? -1 : parseInt(transferSessions, 10) || 0;

    let data = {
      usageId: membershipUsageId,
      transferStartDate: moment(transferDate, 'YYYY.MM.DD').format('YYYY-MM-DD'),
      validityDays: validityDays + 1,
      totalMax: totalMaxValue
    };

    if (activeTab === 'existing') {
      if (!selectedTransferee) {
        alert(t('memberDetail.assignModalAlertNoTransferee'));
        return;
      }
      data.memberId = selectedTransferee.memberId;
    }
    else {
      if (!name) {
        alert(t('memberRegistration.alertNameRequired'));
        return;
      }
      if (!noHavePhone && (!phone || phone.length < 6)) {
        alert(t('memberRegistration.alertPhoneRequired'));
        return;
      }
      if (!birth || birth.length !== 8) {
        alert(t('memberRegistration.alertBirthRequired'));
        return;
      }
      data = {
        ...data,
        memberId: 0,
        name: name,
        phone: phone,
        noHavePhone: noHavePhone,
        countryCode: selectedCountry.countryCode,
        countryIso: selectedCountry.countryIso || selectedCountry.iso || null,
        gender: gender,
        birth: birth,
        profile: profile || '',
        userId: userId || ''
      };
    }

    const isNewTab = activeTab === 'new';

    if (isNewTab) setIsSavingTransfer(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('membership/usage/transfer', 'POST', data);

        if (isNewTab) setIsSavingTransfer(false);

        if (responseData.status === 200) {
          if (responseData.data?.newMemberCreated) alert(t('memberDetail.assignModalAlertSuccessNew'));
          else alert(t('memberDetail.assignModalAlertSuccess'));
          if (rendering) rendering();
          exitClick();
        }
        else if (responseData.status === 201) alert(t('memberDetail.assignModalAlertInvalid'));
        else if (responseData.status === 202) alert(t('memberDetail.assignModalAlertNoPermission'));
        else if (responseData.status === 203) alert(t('memberDetail.assignModalAlertDeleted'));
        else if (responseData.status === 205) {
          alert(t('memberDetail.alertAssignUnpaidBlocked'));
          if (rendering) rendering();
          exitClick();
        }
        else if (responseData.status === 204) {
          alert(t('memberDetail.assignModalAlertDuplicatePhone'));
          const phoneDigits = (phone || '').replace(/\D/g, '');
          const birthLast6 = (birth || '').slice(-6);
          const targetCountryCode = selectedCountry?.countryCode || '';
          const matched = transfereeList.find(m =>
            (m.name || '') === (name || '')
            && (m.phone || '').replace(/\D/g, '') === phoneDigits
            && (m.birth || '').slice(-6) === birthLast6
            && (m.countryCode || '') === targetCountryCode
          );
          if (matched) {
            setSelectedTransferee(matched);
            setSearchValue('');
            setIsSearchOpen(false);
          }
          setActiveTab('existing');
        }
        else if (responseData.status === 401) alert(t('memberRegistration.alertLoginRequired'));
      }
    )();
  }

  function formatMembershipPeriod(start, end) {
    if (!start || !end) return '-';
    return `${moment(start).format(t('calendarInput.shortDateFormat'))} - ${moment(end).format(t('calendarInput.shortDateFormat'))}`;
  }

  return (
    <TotalModalWrapper>
      {
        isSavingTransfer &&
        <SavingOverlay>
          <LottieWrapper>
            <Lottie animationData={LoadingAnimation} loop />
          </LottieWrapper>
        </SavingOverlay>
      }
      <ModalView $activeTab={activeTab}>
        <TitleWrapper>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} cursor='default'>{t('memberDetail.assignModalTitle')}</Text>
        </TitleWrapper>
        <ContentWrapper $activeTab={activeTab}>
          <LeftContentWrapper>
            <TextWrapper height={24}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text} cursor='default'>{t('memberDetail.assignModalFrom')}</Text>
            </TextWrapper>
            <ProfileWrapper>
              <ProfileBox>
                <UserProfile profile={memberProfile} defaultProfile={DefaultProfile} />
              </ProfileBox>
              <TextWrapper width='100px'>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{memberName}</Text>
              </TextWrapper>
              <TextWrapper width='120px'>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatPhoneNumber(memberPhone, memberCountryCode, languageMode)}</Text>
              </TextWrapper>
            </ProfileWrapper>
            <InnerBox>
              <TextWrapper width='100%'>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.assignModalCurrentMembership')}</Text>
              </TextWrapper>
              <RowDivisionLine />
              <ColumnUnitWrapper width='100%' gap='5px'>
                <TextWrapper width='100%' height='27px'>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text} cursor='default' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{membershipName}</Text>
                </TextWrapper>
                <TextWrapper width='100%' height='21px'>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{membershipPeriod}</Text>
                </TextWrapper>
              </ColumnUnitWrapper>
              <RowUnitWrapper width='100%' gap='10px'>
                <PaymentInfoWrapper width='150px'>
                  <TextWrapper width='100%' height='17px'>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{t('memberDetail.assignModalPaymentAmount')}</Text>
                  </TextWrapper>
                  <PaymentAmountWrapper>
                    <PaymentAmountText>{formattedPrice} -</PaymentAmountText>
                    <PaymentMethodText>{paymentMethodsText}</PaymentMethodText>
                  </PaymentAmountWrapper>
                </PaymentInfoWrapper>
                <PaymentInfoWrapper width='132px'>
                  <TextWrapper width='100%' height='17px'>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{t('memberDetail.assignModalOutstanding')}</Text>
                  </TextWrapper>
                  <TextWrapper width='100%' height='21px'>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formattedNotReceivedPrice}</Text>
                  </TextWrapper>
                </PaymentInfoWrapper>
              </RowUnitWrapper>
            </InnerBox>
            <TransferDateInput>
              <TextWrapper width='100%' height='23px'>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.assignModalTransferDate')}</Text>
              </TextWrapper>
              <CalenderInput value={transferDate} setValue={setTransferDate} clickFn={() => { }} height={36} />
            </TransferDateInput>
          </LeftContentWrapper>
          <ColumnDivisionLineWrapper>
            <ColumnDivisionLine />
          </ColumnDivisionLineWrapper>
          <RightContentWrapper>
            <TextWrapper height={24}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text} cursor='default'>{t('memberDetail.assignModalTo')}</Text>
            </TextWrapper>
            <TabButtonWrapper>
              <TabButton $isActive={activeTab === 'existing'} onClick={() => setActiveTab('existing')}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={activeTab === 'existing' ? theme.subBackground : theme.text} cursor='pointer'>{t('memberDetail.assignModalTabExisting')}</Text>
              </TabButton>
              <TabButton $isActive={activeTab === 'new'} onClick={() => setActiveTab('new')}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={activeTab === 'new' ? theme.subBackground : theme.text} cursor='pointer'>{t('memberDetail.assignModalTabNew')}</Text>
              </TabButton>
            </TabButtonWrapper>
            {
              activeTab === 'existing' &&
              <ExistingTransfereeView
                isSearchOpen={isSearchOpen}
                setIsSearchOpen={setIsSearchOpen}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                selectedTransferee={selectedTransferee}
                onClickTransfereeItem={onClickTransfereeItem}
                filteredTransfereeList={filteredTransfereeList}
                isLoadingTransferees={isLoadingTransferees}
                TransfereeScrollRef={TransfereeScrollRef}
                transferDays={transferDays}
                transferSessions={transferSessions}
                handleTransferDaysChange={handleTransferDaysChange}
                handleTransferSessionsChange={handleTransferSessionsChange}
                remainingDays={remainingDays}
                remainingSessions={remainingSessions}
                isUnlimitedSessions={isUnlimitedSessions}
                computeTransferEndDate={computeTransferEndDate}
                formatMembershipPeriod={formatMembershipPeriod} />
            }
            {
              activeTab === 'new' &&
              <NewTransfereeView
                noHavePhone={noHavePhone}
                onClickNoPhone={onClickNoPhone}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                countryCodeSelectBoxRef={countryCodeSelectBoxRef}
                phone={phone}
                setPhone={setPhone}
                phoneErrorMsg={phoneErrorMsg}
                setPhoneErrorMsg={setPhoneErrorMsg}
                onClickLinkCoachIdCheck={onClickLinkCoachIdCheck}
                isUnregisteredAfterCheck={isUnregisteredAfterCheck}
                name={name}
                setName={setName}
                gender={gender}
                setGender={setGender}
                birth={birth}
                setBirth={setBirth}
                profile={profile}
                setProfile={setProfile}
                userId={userId}
                transferDays={transferDays}
                transferSessions={transferSessions}
                handleTransferDaysChange={handleTransferDaysChange}
                handleTransferSessionsChange={handleTransferSessionsChange}
                remainingDays={remainingDays}
                remainingSessions={remainingSessions}
                isUnlimitedSessions={isUnlimitedSessions}
                computeTransferEndDate={computeTransferEndDate} />
            }
          </RightContentWrapper>
        </ContentWrapper>
        <BottomButtonWrapper>
          <CloseButton onClick={exitClick}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.assignModalClose')}</Text>
          </CloseButton>
          <SaveButton onClick={onClickSave}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.background} cursor='pointer'>{t('memberDetail.assignModalSave')}</Text>
          </SaveButton>
        </BottomButtonWrapper>
      </ModalView>
    </TotalModalWrapper>
  );
}

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 20px 0 20px 20px;

  box-sizing: border-box;
  width: 818px;
  height: ${props => props.$activeTab === 'new' ? '702px' : '522px'};

  background: ${props => props.theme.subBackground};
  border-radius: 10px;

  transition: height 0.3s ease-in-out;
`;

const SavingOverlay = styled.div`
  position: fixed;
  inset: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  background: rgba(0, 0, 0, 0.3);

  z-index: 9999;
`;

const LottieWrapper = styled.div`
  width: 120px;
  height: 120px;
`;

const TitleWrapper = styled.div`
  justify-content: center;

  height: 30px;

  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  margin: 20px 20px 0 0;

  width: 100%;
  height: ${props => props.$activeTab === 'new' ? '572px' : '392px'};

  overflow: hidden;

  transition: height 0.3s ease-in-out;
`;

const LeftContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 312px;

  gap: 15px;
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  
  gap: 10px;
`;

const ProfileBox = styled.div`
  width: 42px;
  height: 42px;

  border-radius: 3px;
  overflow: hidden;

  & img {
    object-fit: cover;
  }
`;

const InnerBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px;

  box-sizing: border-box;
  width: 100%;
  min-height: 111px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.thirdBackground};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.05);
`;

const RowDivisionLine = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 5px;
  background-color: ${props => props.theme.line};
`;

const ColumnUnitWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  ${props => props.width && `width: ${props.width};`}

  gap: ${props => props.gap};
`;

const RowUnitWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  ${props => props.width && `width: ${props.width};`}

  gap: ${props => props.gap};
`;

const PaymentInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: ${props => props.width};

  gap: 5px;
`;

const PaymentAmountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  min-width: 0;
  height: 21px;

  width: 100%;
  height: 21px;

  gap: 3.5px;
`;

const PaymentAmountText = styled.div`
  flex-shrink: 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;
`;

const PaymentMethodText = styled.div`
  flex: 1;
  min-width: 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TransferDateInput = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 5px;
`;

const ColumnDivisionLineWrapper = styled.div`
  display: flex;
  align-self: stretch;

  padding: 20px 20px 40px 20px;
`;

const ColumnDivisionLine = styled.div`
  width: 1px;
  height: 100%;

  border-radius: 5px;
  background-color: ${props => props.theme.line};
`;

const Input = styled.input`
  padding: 0 10px;

  box-sizing: border-box;
  width: 100%;
  height: 32px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  outline: none;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};
  background-color: ${props => props.theme.background};

  &::placeholder {
    color: ${props => props.theme.line};
  }
`;

const BottomButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  box-sizing: border-box;
  padding: 0 20px 0 0;

  width: 100%;

  gap: 10px;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 80px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.subKey};
`;

const RightContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 425px;

  gap: 15px;
`;

const TabButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 10px;

  box-sizing: border-box;
  width: 100%;
  height: 52px;

  gap: 10px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;

const TabButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 197.5px;
  height: 32px;

  border-radius: 5px;
  background-color: ${props => props.$isActive ? props.theme.subKey : props.theme.subBackground};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  ${props => props.width && `width: ${props.width};`}
  height: ${props => props.height};
`;

const Text = styled.div`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
